import { useState, useEffect } from "react";
import {
  Bell,
  AlertTriangle,
  Clock,
  CheckCircle,
  Shield,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { useAlertAnalysis } from "../../../hooks/aiHooks/useAlertAnalysis";
import AIThinkingLoader from "../../common/AIThinkingLoader";

const AIAlertAnalysisCard = ({ vmId }) => {
  const [analysisList, setAnalysisList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasData, setHasData] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { getAllAlertAnalyses } = useAlertAnalysis();

  useEffect(() => {
    fetchAnalysisData();
  }, [vmId]);

  const fetchAnalysisData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAllAlertAnalyses(vmId);
      if (result.success && result.data && result.data.length > 0) {
        setAnalysisList(result.data);
        setHasData(true);
      } else {
        setAnalysisList([]);
        setHasData(false);
      }
    } catch (err) {
      console.error("Error fetching alert analysis:", err);
      setError("Failed to load AI analysis data");
      setHasData(false);
    } finally {
      setLoading(false);
    }
  };

  const getSuggestionActionColor = (action) => {
    switch (action?.toUpperCase()) {
      case "INVESTIGATE":
        return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      case "MONITOR":
        return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "RESCHEDULE":
        return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "SCALE_UP":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const toggleExpand = () => setIsExpanded(!isExpanded);

  if (loading) {
    return <AIThinkingLoader />;
  }

  if (error) {
    return null;
  }

  if (!hasData || analysisList.length === 0) {
    return null;
  }

  return (
    <div
      className="rounded-xl border border-primary-cyan/20 overflow-hidden my-4 sm:my-6"
      style={{ backgroundColor: "#10182b" }}
    >
      <div
        className="px-4 sm:px-6 py-4 border-b border-primary-cyan/20 sticky z-20"
        style={{ backgroundColor: "#10182b" }}
      >
        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-primary-cyan/20 flex items-center justify-center">
              <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
            </div>
            <span className="text-primary-cyan text-sm sm:text-lg font-bold tracking-wide">
              AI ALERT INSIGHTS
            </span>
          </div>
          <button
            onClick={toggleExpand}
            className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-primary-cyan/10 hover:bg-primary-cyan/20 transition-all duration-200 text-primary-cyan text-sm sm:text-base font-semibold"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5" />
                Show Less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
                Show Details
              </>
            )}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-4 sm:p-6" style={{ backgroundColor: "#10182b" }}>
          <div className="space-y-5 sm:space-y-6">
            {analysisList.map((analysis) => {
              const hasAnomaly = analysis.anomalyDetected;
              return (
                <div
                  key={analysis.vmId}
                  className="rounded-xl overflow-hidden border border-primary-cyan/20"
                  style={{ backgroundColor: "#10182b" }}
                >
                  <div className="px-4 sm:px-6 py-4 border-b border-primary-cyan/20">
                    <div className="flex justify-between items-center flex-wrap gap-3">
                      <h4 className="text-white font-bold text-base sm:text-lg break-words">
                        {analysis.vmName}
                      </h4>
                      <div
                        className={`flex items-center gap-2 px-2.5 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold ${hasAnomaly ? "bg-red-500/20 text-red-400 border-red-500/30" : "bg-green-500/20 text-green-400 border-green-500/30"}`}
                      >
                        {hasAnomaly ? (
                          <AlertTriangle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        ) : (
                          <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                        )}
                        <span>
                          {hasAnomaly ? "Anomaly Detected" : "No Anomaly"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 sm:p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
                      <div
                        className="rounded-xl p-4 sm:p-5 transition-all duration-300 hover:shadow-lg hover:shadow-primary-cyan/10 hover:scale-[1.01]"
                        style={{ backgroundColor: "#1c2436" }}
                      >
                        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                            <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                          </div>
                          <span className="text-primary-cyan text-xs sm:text-base font-bold uppercase tracking-wide">
                            Alert Pattern
                          </span>
                        </div>
                        <p className="text-gray-200 text-sm sm:text-base leading-relaxed font-medium">
                          {analysis.alertPatternDescription ||
                            "No pattern detected"}
                        </p>
                        <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-gray-700">
                          <div>
                            <p className="text-gray-500 text-xs sm:text-sm">Frequency</p>
                            <p className="text-gray-300 text-sm sm:text-base font-semibold">
                              {analysis.frequencyPerDay || "N/A"}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs sm:text-sm">
                              Avg Duration
                            </p>
                            <p className="text-gray-300 text-sm sm:text-base font-semibold">
                              {analysis.averageDurationMinutes
                                ? `${analysis.averageDurationMinutes} min`
                                : "N/A"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div
                        className="rounded-xl p-4 sm:p-5 transition-all duration-300 hover:shadow-lg hover:shadow-red-500/10 hover:scale-[1.01]"
                        style={{ backgroundColor: "#1c2436" }}
                      >
                        <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                          </div>
                          <span className="text-primary-cyan text-xs sm:text-base font-bold uppercase tracking-wide">
                            Anomaly
                          </span>
                        </div>
                        {hasAnomaly && analysis.anomalyDetails ? (
                          <p className="text-gray-200 text-sm sm:text-base leading-relaxed font-medium">
                            {analysis.anomalyDetails}
                          </p>
                        ) : (
                          <div className="flex flex-col items-center justify-center py-4 sm:py-6">
                            <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-400 mb-3" />
                            <p className="text-green-400 text-sm sm:text-base font-bold">
                              No Anomaly Detected
                            </p>
                            <p className="text-gray-500 text-xs sm:text-sm mt-1">
                              All alert patterns are normal
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className="mt-5 sm:mt-6 rounded-xl p-4 sm:p-5 transition-all duration-300 hover:shadow-lg hover:shadow-primary-cyan/10 hover:scale-[1.01]"
                      style={{ backgroundColor: "#1c2436" }}
                    >
                      <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-primary-cyan/20 flex items-center justify-center">
                          <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-primary-cyan" />
                        </div>
                        <span className="text-primary-cyan text-xs sm:text-base font-bold uppercase tracking-wide">
                          Suggestion
                        </span>
                      </div>
                      <p className="text-gray-200 text-sm sm:text-lg leading-relaxed font-medium">
                        {analysis.suggestionText || "No suggestion available"}
                      </p>
                      {analysis.suggestionAction &&
                        analysis.suggestionAction !== "NONE" && (
                          <div className="mt-4 sm:mt-5">
                            <span
                              className={`text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-bold ${getSuggestionActionColor(analysis.suggestionAction)}`}
                            >
                              Action:{" "}
                              {analysis.suggestionAction.replace(/_/g, " ")}
                            </span>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAlertAnalysisCard;